import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Clock, User, Tag, MessageSquare, CheckCircle, AlertCircle,
  Circle, Send, Zap, Hash, Mail, Globe, MoreHorizontal, Edit2, UserPlus,
  PlayCircle, XCircle, RefreshCw, ExternalLink, Shield, Copy, ChevronRight,
  Loader2, Lock, StickyNote, AlertTriangle, Eye, EyeOff
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { getTicket, upsertTicket, createApprovalFromTicket, subscribe, isIntegrationConnected, connectIntegration, disconnectIntegration } from '../data/demoStore';
import { getLogoByIntegrationId } from '../constants/logos';

const fallbackTicket = {
  id: 'TKT-1234',
  subject: 'Need access to Salesforce',
  description: 'Hi IT team,\n\nI need access to Salesforce for my new role in the Sales department. I started last week and my manager Sarah told me to request access here.\n\nThanks,\nSarah Chen',
  requester: {
    name: 'Sarah Chen',
    email: 'sarah@company.com',
    avatar: 'SC',
    department: 'Sales',
    manager: 'Mike Torres',
    startDate: '2024-01-08'
  },
  channel: { type: 'slack', name: '#it-help' },
  category: 'access',
  priority: 'medium',
  status: 'open',
  assignedTo: { type: 'team', name: 'Identity & Access' },
  createdAt: '2024-01-15T10:30:00Z',
  lastActivity: '5 min ago',
  messages: [
    {
      id: 'msg-1',
      author: { name: 'Sarah Chen', avatar: 'SC', type: 'requester' },
      content: 'Hi IT team,\n\nI need access to Salesforce for my new role in the Sales department. I started last week and my manager Sarah told me to request access here.\n\nThanks,\nSarah Chen',
      timestamp: '2024-01-15T10:30:00Z',
      isInternal: false
    },
    {
      id: 'msg-2',
      author: { name: 'Vedaya AI', avatar: 'V', type: 'ai' },
      content: 'Hi Sarah! I can help you get access to Salesforce. I see you\'re in the Sales department and Mike Torres is your manager.\n\nI\'ll need manager approval for this request. I\'ve already sent an approval request to Mike.',
      timestamp: '2024-01-15T10:31:00Z',
      aiAction: { type: 'approval_requested', target: 'Mike Torres' },
      isInternal: false
    },
    {
      id: 'msg-internal-1',
      author: { name: 'Emily Zhang', avatar: 'EZ', type: 'agent' },
      content: 'Checking Okta - user is already in the system from onboarding. Just needs group assignment.',
      timestamp: '2024-01-15T10:32:00Z',
      isInternal: true
    },
    {
      id: 'msg-3',
      author: { name: 'Sarah Chen', avatar: 'SC', type: 'requester' },
      content: 'Great, thanks! How long does approval usually take?',
      timestamp: '2024-01-15T10:35:00Z',
      isInternal: false
    }
  ],
  suggestedActions: [
    {
      id: 'action-1',
      name: 'Grant Salesforce Access',
      workflow: 'Standard App Access',
      confidence: 92,
      requiresApproval: true,
      approver: 'Mike Torres',
      systems: ['Okta', 'Salesforce']
    },
    {
      id: 'action-2',
      name: 'Add to Sales Distribution Lists',
      workflow: 'Group Membership',
      confidence: 78,
      requiresApproval: false,
      systems: ['Google Groups']
    }
  ],
  relatedTickets: [
    { id: 'TKT-1120', subject: 'Salesforce access for new sales rep', status: 'resolved' },
    { id: 'TKT-1098', subject: 'Add to Sales team groups', status: 'resolved' }
  ],
  auditLog: [
    { action: 'Ticket created', timestamp: '2024-01-15T10:30:00Z', actor: 'System' },
    { action: 'AI classified as access request', timestamp: '2024-01-15T10:30:05Z', actor: 'Vedaya AI' },
    { action: 'Routed to Identity & Access team', timestamp: '2024-01-15T10:30:10Z', actor: 'Routing Engine' },
    { action: 'Approval requested from Mike Torres', timestamp: '2024-01-15T10:31:00Z', actor: 'Vedaya AI' }
  ]
};

const statusConfig = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Circle },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800 border-amber-200', icon: RefreshCw },
  pending_approval: { label: 'Pending Approval', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Clock },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-slate-100 text-slate-800 border-slate-200', icon: CheckCircle }
};

const priorityConfig = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-800' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-800' },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-600' }
};

export const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  // Load ticket from demo store or fallback
  const [ticket, setTicket] = useState(() => getTicket(ticketId) || fallbackTicket);

  const [replyText, setReplyText] = useState('');
  const [status, setStatus] = useState(ticket.status);
  const [priority, setPriority] = useState(ticket.priority);
  const [isInternal, setIsInternal] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  // Subscribe to store changes
  useEffect(() => {
    return subscribe(() => {
      const updated = getTicket(ticketId);
      if (updated) {
        setTicket(updated);
        setStatus(updated.status);
        setPriority(updated.priority);
      }
    });
  }, [ticketId]);

  // Persist status changes
  const handleStatusChange = useCallback((newStatus) => {
    setStatus(newStatus);
    const updated = { ...ticket, status: newStatus, lastActivity: 'Just now' };
    upsertTicket(updated);
    toast.success(`Status changed to ${statusConfig[newStatus]?.label || newStatus}`);
  }, [ticket]);

  // Persist priority changes
  const handlePriorityChange = useCallback((newPriority) => {
    setPriority(newPriority);
    const updated = { ...ticket, priority: newPriority, lastActivity: 'Just now' };
    upsertTicket(updated);
  }, [ticket]);
  const statusCfg = statusConfig[status];
  const StatusIcon = statusCfg.icon;

  const filteredMessages = showInternalNotes
    ? ticket.messages
    : ticket.messages.filter(m => !m.isInternal);

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    setIsSending(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success(isInternal ? 'Internal note added' : 'Reply sent');
    setReplyText('');
    setIsSending(false);
  };

  const handleCloseTicket = async () => {
    const updated = { ...ticket, status: 'closed', lastActivity: 'Just now' };
    upsertTicket(updated);
    setStatus('closed');
    setShowCloseDialog(false);
    toast.success('Ticket closed');
  };

  const [showApprovalPrompt, setShowApprovalPrompt] = useState(false);
  const [createdApprovalId, setCreatedApprovalId] = useState(null);
  const [showConnectPrompt, setShowConnectPrompt] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [oktaConnected, setOktaConnected] = useState(isIntegrationConnected('okta'));

  // Subscribe to integration changes
  useEffect(() => {
    const checkIntegration = () => setOktaConnected(isIntegrationConnected('okta'));
    window.addEventListener('vedaya-demo-store', checkIntegration);
    return () => window.removeEventListener('vedaya-demo-store', checkIntegration);
  }, []);

  const handleConnectOkta = async () => {
    setIsConnecting(true);
    await new Promise(resolve => setTimeout(resolve, 1200));
    connectIntegration('okta');
    setOktaConnected(true);
    setIsConnecting(false);
    setShowConnectPrompt(false);
    toast.success('Okta connected!', {
      description: 'You can now execute actions.',
    });
    // Automatically open the execute dialog after connecting
    if (selectedAction) {
      setShowExecuteDialog(true);
    }
  };

  const handleExecuteAction = async () => {
    if (!selectedAction) return;
    setIsExecuting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));

    if (selectedAction.requiresApproval) {
      // Create approval in demo store
      const approval = createApprovalFromTicket({
        ticketId: ticket.id,
        title: selectedAction.name,
        description: `Requested for ${ticket.requester?.name || 'Unknown'}: ${ticket.subject}`,
        actions: [{ name: selectedAction.name, systems: selectedAction.systems }],
        risk: selectedAction.confidence > 85 ? 'low' : 'medium',
      });

      // Update ticket status
      const updated = { ...ticket, status: 'pending_approval', lastActivity: 'Just now' };
      upsertTicket(updated);

      setIsExecuting(false);
      setShowExecuteDialog(false);

      // Show approval prompt dialog instead of toast
      setCreatedApprovalId(approval.id);
      setShowApprovalPrompt(true);
    } else {
      // Direct execution - mark as in progress then resolved
      const updated = { ...ticket, status: 'resolved', lastActivity: 'Just now' };
      upsertTicket(updated);
      toast.success(`Action "${selectedAction.name}" executed successfully`);
      setIsExecuting(false);
      setShowExecuteDialog(false);
    }
  };

  const openExecuteDialog = (action) => {
    setSelectedAction(action);
    // Check if integration is connected before showing execute dialog
    if (!oktaConnected) {
      setShowConnectPrompt(true);
    } else {
      setShowExecuteDialog(true);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && replyText.trim() && !isSending) {
        e.preventDefault();
        handleSendReply();
      }
      if (e.key === 'Escape') {
        navigate('/tickets');
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyText, navigate, isSending]);

  return (
    <div className="min-h-full bg-background">
      <div className="border-b bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/tickets" aria-label="Back to tickets">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-4 h-4" aria-hidden="true" />
              </Button>
            </Link>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono text-muted-foreground">{ticket.id}</span>
                <Badge variant="outline" className={cn("text-xs", priorityConfig[priority].color)}>
                  {priorityConfig[priority].label}
                </Badge>
                <Badge variant="outline" className={cn("text-xs", statusCfg.color)}>
                  <StatusIcon className="w-3 h-3 mr-1" aria-hidden="true" />
                  {statusCfg.label}
                </Badge>
              </div>
              <h1 className="text-lg font-semibold text-foreground">{ticket.subject}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Select value={status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-40" aria-label="Change ticket status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(statusConfig).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="More actions">
                    <MoreHorizontal className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem>
                    <Edit2 className="w-4 h-4 mr-2" aria-hidden="true" />
                    Edit Ticket
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <UserPlus className="w-4 h-4 mr-2" aria-hidden="true" />
                    Reassign
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Copy className="w-4 h-4 mr-2" aria-hidden="true" />
                    Duplicate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive"
                    onClick={() => setShowCloseDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                    Close Ticket
                  </DropdownMenuItem>
                  {oktaConnected && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          disconnectIntegration('okta');
                          toast.success('Okta disconnected');
                          setOktaConnected(false);
                        }}
                      >
                        <Shield className="w-4 h-4 mr-2" aria-hidden="true" />
                        Disconnect Okta
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-8 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Conversation</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowInternalNotes(!showInternalNotes)}
                    className="text-xs"
                  >
                    {showInternalNotes ? (
                      <>
                        <Eye className="w-3 h-3 mr-1" aria-hidden="true" />
                        Showing internal notes
                      </>
                    ) : (
                      <>
                        <EyeOff className="w-3 h-3 mr-1" aria-hidden="true" />
                        Hiding internal notes
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {filteredMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex gap-3",
                      msg.author.type === 'ai' && "bg-primary/5 -mx-6 px-6 py-4",
                      msg.isInternal && "bg-amber-50 border-l-4 border-amber-400 -mx-6 px-6 py-4"
                    )}
                    role="article"
                    aria-label={`Message from ${msg.author.name}`}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0",
                      msg.author.type === 'ai' ? "bg-primary text-primary-foreground" : "bg-muted"
                    )}>
                      {msg.author.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-foreground">{msg.author.name}</span>
                        {msg.author.type === 'ai' && (
                          <Badge variant="secondary" className="text-xs">AI</Badge>
                        )}
                        {msg.isInternal && (
                          <Badge variant="outline" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                            <Lock className="w-3 h-3 mr-1" aria-hidden="true" />
                            Internal
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm text-foreground whitespace-pre-wrap">{msg.content}</p>
                      {msg.aiAction && (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                            <Zap className="w-3 h-3 mr-1" aria-hidden="true" />
                            {msg.aiAction.type === 'approval_requested'
                              ? `Approval requested from ${msg.aiAction.target}`
                              : msg.aiAction.type}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                <Separator />

                <div className="space-y-3">
                  {isInternal && (
                    <Alert className="bg-amber-50 border-amber-200">
                      <Lock className="h-4 w-4 text-amber-600" />
                      <AlertDescription className="text-amber-800">
                        This note will only be visible to agents, not to the requester.
                      </AlertDescription>
                    </Alert>
                  )}
                  <Textarea
                    placeholder={isInternal ? "Add an internal note..." : "Type your reply..."}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={3}
                    aria-label={isInternal ? "Internal note" : "Reply message"}
                    className={cn(isInternal && "border-amber-300 focus:ring-amber-400")}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant={isInternal ? "secondary" : "outline"}
                        size="sm"
                        onClick={() => setIsInternal(!isInternal)}
                        className={cn(isInternal && "bg-amber-100 hover:bg-amber-200 text-amber-800")}
                      >
                        <StickyNote className="w-4 h-4 mr-2" aria-hidden="true" />
                        {isInternal ? "Internal Note" : "Public Reply"}
                      </Button>
                      <Button variant="outline" size="sm">
                        <Zap className="w-4 h-4 mr-2" aria-hidden="true" />
                        AI Assist
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+Enter to send
                      </span>
                      <Button
                        disabled={!replyText.trim() || isSending}
                        onClick={handleSendReply}
                        className={cn(isInternal && "bg-amber-600 hover:bg-amber-700")}
                      >
                        {isSending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" aria-hidden="true" />
                        )}
                        {isInternal ? "Add Note" : "Send Reply"}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="w-4 h-4 text-primary" aria-hidden="true" />
                  Suggested Actions
                </CardTitle>
                <CardDescription>AI-recommended actions based on ticket context</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {ticket.suggestedActions.map((action) => (
                  <div
                    key={action.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-muted/30"
                    role="article"
                    aria-label={`Suggested action: ${action.name}`}
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-sm font-medium text-foreground">{action.name}</h4>
                        <Badge variant="secondary" className="text-xs">{action.confidence}% match</Badge>
                        {action.requiresApproval && (
                          <Badge variant="outline" className="text-xs">
                            <Shield className="w-3 h-3 mr-1" aria-hidden="true" />
                            Requires Approval
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span>Workflow: {action.workflow}</span>
                        <span>Systems: {action.systems.join(', ')}</span>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => openExecuteDialog(action)}>
                      <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                      Execute
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <div className="col-span-4 space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Ticket Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Requester</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                      {ticket.requester.avatar}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{ticket.requester.name}</p>
                      <p className="text-xs text-muted-foreground">{ticket.requester.email}</p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Department</p>
                    <p className="text-sm text-foreground">{ticket.requester.department}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Manager</p>
                    <p className="text-sm text-foreground">{ticket.requester.manager}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Start Date</p>
                    <p className="text-sm text-foreground">{ticket.requester.startDate}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Category</p>
                    <Badge variant="outline" className="capitalize">{ticket.category}</Badge>
                  </div>
                </div>

                <Separator />

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Channel</p>
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{ticket.channel.name}</span>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{ticket.assignedTo.name}</Badge>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-1">Priority</p>
                  <Select value={priority} onValueChange={handlePriorityChange}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(priorityConfig).map(([key, cfg]) => (
                        <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Related Tickets</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {ticket.relatedTickets.map((related) => (
                  <Link
                    key={related.id}
                    to={`/tickets/${related.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div>
                      <p className="text-xs font-mono text-muted-foreground">{related.id}</p>
                      <p className="text-sm text-foreground truncate">{related.subject}</p>
                    </div>
                    <Badge variant="outline" className="text-xs capitalize">{related.status}</Badge>
                  </Link>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Audit Log</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ticket.auditLog.map((entry, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-2 h-2 rounded-full bg-muted-foreground mt-1.5 shrink-0" />
                      <div>
                        <p className="text-sm text-foreground">{entry.action}</p>
                        <p className="text-xs text-muted-foreground">
                          {entry.actor} · {new Date(entry.timestamp).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Ticket</DialogTitle>
            <DialogDescription>
              Are you sure you want to close this ticket? This will notify the requester that the ticket has been resolved.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Open actions will be cancelled. Make sure all required work is complete.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleCloseTicket}>
              <XCircle className="w-4 h-4 mr-2" aria-hidden="true" />
              Close Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showExecuteDialog} onOpenChange={setShowExecuteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Execute Action</DialogTitle>
            <DialogDescription>
              Review the action details before executing.
            </DialogDescription>
          </DialogHeader>
          {selectedAction && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-medium text-foreground mb-2">{selectedAction.name}</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p><strong>Workflow:</strong> {selectedAction.workflow}</p>
                  <p><strong>Systems affected:</strong> {selectedAction.systems.join(', ')}</p>
                  <p><strong>Confidence:</strong> {selectedAction.confidence}%</p>
                </div>
              </div>

              {selectedAction.requiresApproval && (
                <Alert>
                  <Shield className="h-4 w-4" />
                  <AlertDescription>
                    This action requires approval from <strong>{selectedAction.approver}</strong>.
                    An approval request will be sent after you confirm.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExecuteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleExecuteAction} disabled={isExecuting}>
              {isExecuting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                  Executing...
                </>
              ) : (
                <>
                  <PlayCircle className="w-4 h-4 mr-2" aria-hidden="true" />
                  {selectedAction?.requiresApproval ? 'Request Approval' : 'Execute Now'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showApprovalPrompt} onOpenChange={setShowApprovalPrompt}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Approval Request Sent
            </DialogTitle>
            <DialogDescription>
              Your request has been submitted and is awaiting manager approval.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">What happens next?</span>
              </div>
              <ol className="text-sm text-green-700 space-y-1 ml-6 list-decimal">
                <li>The manager will review and approve the request</li>
                <li>Once approved, the action will execute automatically</li>
                <li>You'll be notified when the request is complete</li>
              </ol>
            </div>
            <p className="text-sm text-muted-foreground">
              <strong>Demo tip:</strong> Go to the Approvals page to approve the request as the manager.
            </p>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowApprovalPrompt(false)}>
              Stay here
            </Button>
            <Button onClick={() => navigate('/approvals')}>
              <ArrowRight className="w-4 h-4 mr-2" />
              Go to Approvals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showConnectPrompt} onOpenChange={setShowConnectPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Connect Integration to Execute
            </DialogTitle>
            <DialogDescription>
              To execute this action, Vedaya needs to connect to your identity provider.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 border rounded-lg bg-muted/30">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-white border flex items-center justify-center p-2">
                  {(() => {
                    const OktaLogo = getLogoByIntegrationId('okta');
                    return OktaLogo ? <img src={OktaLogo.src} alt={OktaLogo.alt} className="w-8 h-8" /> : <Shield className="w-6 h-6 text-muted-foreground" />;
                  })()}
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-foreground">Okta</h4>
                  <p className="text-xs text-muted-foreground">Identity & Access Management</p>
                </div>
                {oktaConnected ? (
                  <Badge className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge variant="outline">Not connected</Badge>
                )}
              </div>
            </div>
            <div className="text-sm text-muted-foreground space-y-2">
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>One-click connection (no credentials needed for demo)</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>Enables automated user provisioning</span>
              </p>
              <p className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                <span>Required for access request workflows</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnectPrompt(false)}>
              Cancel
            </Button>
            <Button onClick={handleConnectOkta} disabled={isConnecting}>
              {isConnecting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Connect Okta
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetail;
